import * as d3 from 'd3';
import moment from 'moment';

import { D3SvgGSelection } from './d3-type-aliases';
import { D3GraphPadding, D3GraphPaddingProp } from './DiseaseActivityModels';

export type XAxisTickDimensions = {
  length: number;
  thickness: number;
};

export function roundUpMag(x: number): number {
  // prettier-ignore
  const thresholds = [
    10, 20, 40, 50, 60, 80,
    100, 150, 200, 400, 500, 600, 800,
    1000, 1500, 2000, 4000, 5000, 6000, 8000,
    10000, 15000, 20000, 40000, 50000, 60000, 80000,
    100000, 150000, 200000, 400000, 500000, 600000, 800000,
    1000000, 1500000, 2000000, 4000000, 5000000, 6000000, 8000000,
    10000000, 15000000, 20000000, 40000000, 50000000, 60000000, 80000000,
    100000000, 150000000, 200000000, 400000000, 500000000, 600000000, 800000000,
    1000000000, 1500000000, 2000000000, 4000000000, 5000000000, 6000000000, 8000000000,
    10000000000, 15000000000, 20000000000, 40000000000, 50000000000, 60000000000, 80000000000,
    100000000000, 150000000000, 200000000000, 400000000000, 500000000000, 600000000000, 800000000000,
  ];
  for (const thresh of thresholds) {
    if (x <= thresh) return thresh;
  }
  return x;
}
export function Prop2d3GraphPadding(padding: D3GraphPaddingProp): D3GraphPadding {
  return typeof padding === 'number'
    ? { top: padding, right: padding, bottom: padding, left: padding }
    : padding;
}

/**
 * @returns [barsAll, barsEntered, barsExited]
 */
export function d3EnterExitBars<T>(
  gNode: D3SvgGSelection,
  data: T[],
  asElement = 'rect'
): [
  d3.Selection<d3.BaseType, T, SVGGElement, unknown>,
  d3.Selection<d3.BaseType, T, SVGGElement, unknown>,
  d3.Selection<d3.BaseType, T, SVGGElement, unknown>
] {
  const barsAllOld = gNode.selectAll('.bar').data(data);
  const barsExited = barsAllOld.exit<T>().remove();
  const barsEntered = barsAllOld.enter().append(asElement).attr('class', 'bar');
  const barsAll = gNode.selectAll('.bar').data(data);
  return [barsAll, barsEntered, barsExited];
}

export function d3SetTickLabelsInward<PElement extends d3.BaseType>(
  selection: d3.Selection<d3.BaseType, unknown, PElement, unknown>
) {
  const length = selection.size();
  selection.each(function (x, i) {
    // d3.select(this).select('text').attr('font-size', '0.3em'); // NOTE: debug!!!
    if (i === 0) {
      d3.select(this).select('text').attr('text-anchor', 'start');
    } else if (i === length - 1) {
      d3.select(this).select('text').attr('text-anchor', 'end');
    }
  });
}

export function d3SetTickAlignment<PElement extends d3.BaseType>(
  anchor: 'start' | 'middle' | 'end'
) {
  return function (selection: d3.Selection<d3.BaseType, unknown, PElement, unknown>) {
    selection.selectAll('text').attr('text-anchor', anchor);
  };
}

export function d3SetTickStyle<PElement extends d3.BaseType>(config: XAxisTickDimensions) {
  return function (selection: d3.Selection<d3.BaseType, unknown, PElement, unknown>) {
    selection
      .selectAll('.tick line')
      .attr('stroke-width', config.thickness)
      .attr('y2', config.length);
  };
}

/**
 * For x-axis replaces the `path` of the "┎┒"-looking axis (.domain) with a
 * horizontal line of specified length.
 * For y-axis replaces the `path` of the "C"-looking axis (.domain) with a
 * vertical line of specified length.
 * Both achieve the removal or "corner ticks"
 */
export function d3AxisRemoveCornerTicks<PElement extends d3.BaseType>(config: {
  axisType: 'x-axis' | 'y-axis';
  axisLength: number;
  axisColor: string;
}) {
  const { axisType, axisLength, axisColor } = config;
  return function (selection: d3.Selection<d3.BaseType, unknown, PElement, unknown>) {
    selection.select('.domain').remove();
    if (axisType === 'x-axis')
      selection
        .append('line')
        .attr('class', '.domain')
        .attr('x2', axisLength)
        .attr('stroke', axisColor);
    if (axisType === 'y-axis')
      selection
        .append('line')
        .attr('class', '.domain')
        .attr('y2', axisLength)
        .attr('stroke', axisColor);
  };
}

/**
 * Removes tick lines and allows to adjust position of the tick label text
 */
export function removeTickLines<PElement extends d3.BaseType>(config: {
  textNewX?: number;
  textNewY?: number;
}) {
  const { textNewX, textNewY } = config;
  return function (selection: d3.Selection<d3.BaseType, unknown, PElement, unknown>) {
    selection.selectAll('.tick line').remove();
    textNewX && selection.selectAll('.tick text').attr('x', textNewX);
    textNewY && selection.selectAll('.tick text').attr('y', textNewY);
  };
}

export function d3AlignLastTickWithRhsOfLastBar(barWidth: number) {
  return function (selection: d3.Selection<d3.BaseType, unknown, SVGGElement, unknown>) {
    const length = selection.size();
    selection.each(function (x, i) {
      if (i === length - 1) {
        d3.select(this)
          .select('line')
          .attr('x1', barWidth - 1)
          .attr('x2', barWidth - 1);
        d3.select(this)
          .select('text')
          .attr('dx', barWidth - 1);
      }
    });
  };
}

export function d3ComputeBarChartMeasurementsXAxis(
  minDate: Date,
  maxDate: Date,
  width: number,
  barThicknessMin: number,
  barThicknessMax: number,
  aggregationDays: number
): [number, number, number] {
  const nDays = moment(maxDate).diff(moment(minDate), 'days') + 1;
  const barTotalRoom = (width - barThicknessMax) / (nDays / aggregationDays);
  const [barWidth, barMargin] =
    barTotalRoom > barThicknessMax
      ? [barThicknessMax, barTotalRoom - barThicknessMax]
      : barTotalRoom > barThicknessMax / 2
      ? [barTotalRoom - 2, 2]
      : barTotalRoom > barThicknessMin
      ? [barTotalRoom - 1, 1]
      : [barTotalRoom, 0];
  return [barWidth, barMargin, nDays];
}

export function d3ApplyAxisTypogaphy<PElement extends d3.BaseType>(
  axisLabelColor: string,
  typography: string = null
) {
  return function (selection: d3.Selection<d3.BaseType, unknown, PElement, unknown>) {
    switch (typography) {
      case 'caption':
        return selection
          .selectAll('.tick text')
          .attr('color', axisLabelColor)
          .style('font-family', "'Source Sans Pro', 'Helvetica Neue', Arial, Helvetica, sans-serif")
          .style('font-style', 'normal')
          .style('font-weight', '400')
          .style('font-size', '12px')
          .style('font-size', '14px');
      default:
        return selection
          .selectAll('.tick text')
          .attr('color', axisLabelColor)
          .style('font-family', "'Source Sans Pro', 'Helvetica Neue', Arial, Helvetica, sans-serif")
          .style('font-style', 'normal')
          .style('font-weight', '400')
          .style('font-size', '9px')
          .style('line-height', '14px');
    }
  };
}
